<!--
Copyright (C) 2025 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->

<script setup lang="ts">
import type { WelcomeCards } from 'cmk-shared-typing/typescript/welcome'
import type { Ref } from 'vue'

import usei18n from '@/lib/i18n'
import usePersistentRef from '@/lib/usePersistentRef.ts'

import CmkAccordionStepPanelItem from '@/components/CmkAccordionStepPanel/CmkAccordionStepPanelItem.vue'
import CmkAlertBox from '@/components/CmkAlertBox.vue'
import CmkLinkCard from '@/components/CmkLinkCard'
import CmkWizard, { CmkWizardButton, CmkWizardStep } from '@/components/CmkWizard'
import CmkHeading from '@/components/typography/CmkHeading.vue'

import StepCardsRow from '@/welcome/components/steps/components/StepCardsRow.vue'
import StepParagraph from '@/welcome/components/steps/components/StepParagraph.vue'
import { type StepId } from '@/welcome/components/steps/utils.ts'

const { _t } = usei18n()

const props = defineProps<{
  step: number
  stepId: StepId
  cards: WelcomeCards
  accomplished: boolean
}>()
const emit = defineEmits(['step-completed'])

const currentStep: Ref<number> = usePersistentRef<number>(
  `${props.stepId}-currentStep`,
  0,
  (v) => v as number,
  'local'
)
</script>

<template>
  <CmkAccordionStepPanelItem
    :step="step"
    :disabled="false"
    :accomplished="accomplished"
    :title="_t('Enable notifications')"
    :info="_t('2 Steps | Takes 5-7 min')"
  >
    <StepParagraph>
      {{
        _t(
          `Notifications help you to stay on top of problems without having to constantly check the interface.
            In Checkmk, they are rule-based, enabling you to create a notification system that evolves alongside
            your environment.`
        )
      }}
      <br />
      {{ _t('Learn more about notifications in the') }}
      <a :href="cards.intro_notifications" target="_blank">
        {{ _t('documentation.') }}
      </a>
    </StepParagraph>

    <CmkAlertBox :heading="_t('Before enabling notifications')">
      {{
        _t(
          `Notifications are powerful, but turning them on too early can quickly lead to alert fatigue.
          Take some time to understand your monitoring first. Then start with a small pilot by enabling
          email notifications for a limited number of users. This helps you fine-tune rules and thresholds
          before involving everyone. A gradual rollout ensures notifications stay meaningful and don’t get
          lost in the noise.`
        )
      }}
      <br /><br />
      {{ _t('Tip: To use email notifications, make sure an email server is configured.') }}
    </CmkAlertBox>

    <CmkWizard v-model="currentStep" mode="guided">
      <CmkWizardStep :index="0" :is-completed="() => currentStep > 0 || accomplished">
        <template #header>
          <CmkHeading type="h3">{{ _t('Create a notification rule') }}</CmkHeading>
        </template>
        <template #content>
          <StepParagraph>
            {{
              _t(
                'Follow the step-by-step guide in Setup > Notifications to set up notification rules.'
              )
            }}
          </StepParagraph>
          <StepCardsRow>
            <CmkLinkCard
              icon-name="notifications"
              :title="_t('Add notification rule')"
              :url="cards.add_notification_rule"
              :open-in-new-tab="false"
            />
          </StepCardsRow>
        </template>
        <template #actions>
          <CmkWizardButton type="next" :override-label="_t('Continue')" />
        </template>
      </CmkWizardStep>

      <CmkWizardStep :index="1" :is-completed="() => currentStep > 1 || accomplished">
        <template #header>
          <CmkHeading type="h3">{{ _t('Send a test notification') }}</CmkHeading>
        </template>
        <template #content>
          <StepParagraph>
            {{
              _t(
                'After creation, test your notification rule to make sure alerts reach you the way you expect.'
              )
            }}
          </StepParagraph>
          <StepCardsRow>
            <CmkLinkCard
              icon-name="analysis"
              :title="_t('Test notifications')"
              :url="cards.test_notifications"
              :open-in-new-tab="false"
            />
          </StepCardsRow>
        </template>
        <template #actions>
          <CmkWizardButton
            v-if="!accomplished && stepId"
            type="finish"
            :override-label="_t('Mark as done')"
            @click="emit('step-completed', stepId)"
          />
          <CmkWizardButton type="previous" />
        </template>
      </CmkWizardStep>
    </CmkWizard>
  </CmkAccordionStepPanelItem>
</template>
